/* The page's original product form owns all cart requests. This component only proxies its button. */
if (!customElements.get('gate-mobile-buybar')) {
  customElements.define('gate-mobile-buybar', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.media = matchMedia('(max-width: 749px)');
      this.price = this.querySelector('[data-buybar-price]');
      this.selection = this.querySelector('[data-buybar-selection]');
      this.action = this.querySelector('[data-buybar-submit]');
      this.label = this.querySelector('[data-buybar-label]');
      this.options = this.querySelector('[data-buybar-options]');
      this.unsubscribers = [];
      const on = (target, event, listener, options = {}) =>
        target?.addEventListener(event, listener, { ...options, signal: this.abort.signal });
      on(this.action, 'click', () => this.purchase());
      on(this.options, 'click', () => this.showOptions());
      on(window, 'scroll', () => this.schedule(), { passive: true });
      on(window, 'resize', () => this.schedule(), { passive: true });
      on(window, 'pageshow', () => this.schedule());
      on(window.visualViewport, 'resize', () => this.schedule(), { passive: true });
      on(document, 'change', () => this.schedule());
      on(document, 'input', () => this.schedule());
      on(document, 'focusin', () => this.schedule());
      on(document, 'focusout', () => this.schedule());
      on(document, 'shopify:section:load', () => this.schedule());
      on(this.media, 'change', () => this.schedule());
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(document.body, {
        childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'hidden', 'open', 'aria-hidden']
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        ['variantChange', 'quantityUpdate', 'cartUpdate'].forEach(key => {
          if (PUB_SUB_EVENTS[key]) this.unsubscribers.push(subscribe(PUB_SUB_EVENTS[key], () => this.schedule()));
        });
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartError, () => {
          if (this.submitting) {
            this.submitting = false;
            this.showOptions(true);
          }
        }));
      }
      this.schedule();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.unsubscribers?.forEach(unsubscribe => unsubscribe());
      cancelAnimationFrame(this.frame);
      document.body.classList.remove('gate-buybar-enabled');
      document.body.style.removeProperty('--gate-buybar-space');
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    visible(element) {
      if (!element?.isConnected || !element.getClientRects().length) return false;
      const style = getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }

    source() {
      const main = document.getElementById('MainContent');
      if (!main) return null;
      const husar = main.querySelector('gate-raptor');
      const guru = main.querySelector('[data-guru-page]');
      const native = main.querySelector('product-info[id^="MainProduct-"]');
      const root = husar || guru || native;
      if (!root) return null;
      const form = root.querySelector(husar ? 'form.rp-buy-form' : guru ? 'form.guru-buy-form' : 'form[data-type="add-to-cart-form"]');
      const button = form?.querySelector('button[name="add"], button[type="submit"]');
      if (!form || !button || !this.visible(button)) return null;
      if (guru?.dataset.pricePending === 'true') return null;
      const variant = form.querySelector('[name="id"]:checked') || form.querySelector('[name="id"]');
      return { root, form, button, variant, design: husar ? 'husar' : guru ? 'guru' : 'standard' };
    }

    text(element) {
      return element?.textContent.replace(/\s+/g, ' ').trim() || '';
    }

    priceText(source) {
      if (source.design === 'husar') return this.text(source.root.querySelector('[data-rp-price]'));
      if (source.design === 'guru') return source.variant?.dataset.price || '';
      const priceRoot = source.root.querySelector('[id^="price-"] .price') || source.root.querySelector('.product__info-container .price');
      const selector = priceRoot?.classList.contains('price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular';
      return this.text(priceRoot?.querySelector(selector));
    }

    selectionText(source) {
      let text = '';
      if (source.design === 'husar') {
        const option = source.root.querySelector('.rp-option[aria-current="page"] .rp-option-title');
        text = [source.root.dataset.model, this.text(option)].filter(Boolean).join(' · ');
      } else if (source.design === 'guru') {
        text = source.variant?.dataset.title || '';
      } else {
        const values = [...source.root.querySelectorAll('variant-selects fieldset input:checked, variant-selects select')]
          .map(input => input.tagName === 'SELECT' ? this.text(input.selectedOptions[0]) : input.value)
          .map(value => value.startsWith(this.dataset.productTitle) ? value.slice(this.dataset.productTitle.length).trim().replace(/^[·–—-]\s*/, '') : value)
          .filter(Boolean);
        text = values.join(' · ');
      }
      if (!text) text = this.dataset.productTitle;
      const quantity = source.form.elements.namedItem('quantity') || source.root.querySelector('input[name="quantity"]');
      if (quantity && Number(quantity.value) > 1) text += ` · ${this.dataset.quantityLabel}: ${quantity.value}`;
      return text;
    }

    buttonLabel(button) {
      const copy = button.cloneNode(true);
      copy.querySelectorAll('svg, .loading__spinner, [aria-hidden="true"], .hidden, [hidden]').forEach(node => node.remove());
      return this.text(copy);
    }

    setText(element, value) {
      if (element.textContent !== value) element.textContent = value;
    }

    overlayOpen() {
      if (document.body.classList.contains('overflow-hidden') || document.body.classList.contains('overflow-hidden-mobile')) return true;
      return [...document.querySelectorAll('dialog[open], [aria-modal="true"], .drawer.active, .cart-notification.active, header-drawer details[open]')]
        .some(element => this.visible(element) && element.getAttribute('aria-hidden') !== 'true' &&
          !element.closest('[hidden], [inert], details:not([open]), .drawer:not(.active)'));
    }

    keyboardOpen() {
      const field = document.activeElement;
      return !!(field?.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"]), textarea, select, [contenteditable="true"]'));
    }

    update() {
      const source = this.source();
      const enabled = this.media.matches && !!source;
      if (document.body.classList.contains('gate-buybar-enabled') !== enabled) {
        document.body.classList.toggle('gate-buybar-enabled', enabled);
      }
      if (!enabled) {
        this.hide();
        return;
      }
      this.current = source;
      this.dataset.design = source.design;
      this.setText(this.price, this.priceText(source));
      const selection = this.selectionText(source);
      this.setText(this.selection, selection);
      this.selection.title = selection;
      const busy = source.button.classList.contains('loading') || source.button.getAttribute('aria-disabled') === 'true';
      const unavailable = source.button.disabled || source.variant?.disabled || !source.variant?.value;
      this.action.disabled = unavailable || busy;
      this.action.setAttribute('aria-busy', String(busy));
      const label = this.buttonLabel(source.button) || this.dataset.unavailableLabel;
      this.setText(this.label, label);
      if (!busy) this.submitting = false;
      const formId = source.form.getAttribute('id');
      if (formId) this.options.setAttribute('aria-controls', formId);
      const box = source.button.getBoundingClientRect();
      const barHeight = this.offsetHeight || 112;
      const originalInView = box.bottom > 0 && box.top < window.innerHeight - barHeight;
      const hide = originalInView || this.overlayOpen() || this.keyboardOpen();
      this.hidden = hide;
      this.inert = hide;
      if (!hide) {
        const space = `${this.offsetHeight}px`;
        if (document.body.style.getPropertyValue('--gate-buybar-space') !== space) {
          document.body.style.setProperty('--gate-buybar-space', space);
        }
      }
    }

    hide() {
      this.hidden = true;
      this.inert = true;
    }

    showOptions(error = false) {
      const source = this.source();
      if (!source) return;
      const target = error ? source.root.querySelector('.product-form__error-message-wrapper:not([hidden]), .errors') :
        source.root.querySelector('.rp-models, .guru-model-fieldset, variant-selects, .product__info-container');
      const destination = target || source.form;
      const header = document.querySelector('sticky-header');
      const inset = Math.min(header?.getBoundingClientRect().height || 0, 160) + 20;
      window.scrollTo({ top: window.scrollY + destination.getBoundingClientRect().top - inset,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      const focus = destination.querySelector('input:not([type="hidden"]):not(:disabled), select:not(:disabled), button:not(:disabled), a[href]') || source.button;
      focus?.focus({ preventScroll: true });
      this.schedule();
    }

    purchase() {
      const source = this.source();
      if (!source || this.action.disabled) return;
      // Required customisation / recipient fields stay in the native form and remain visible on validation errors.
      if (!source.form.checkValidity()) {
        const invalid = source.form.querySelector(':invalid');
        this.showOptions();
        invalid?.focus({ preventScroll: true });
        invalid?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        source.form.reportValidity();
        return;
      }
      this.submitting = true;
      source.button.click();
      this.schedule();
    }
  });
}
